import { Box, Button, TFDataTable } from '@tf/ui';
import { fmt } from '@tf/utils';
import type React from 'react';
import { useNavigate } from 'react-router-dom';

import type { ProfileWithStatus } from '@/core/api';
import { useApplicationsList } from '@/core/api';
import { getNodeIdByObjectUri } from '@/core/api/legacy';
import { useDefinitions } from '@/core/providers';

import { AddApplicationForm } from './AddApplicationForm';

const applicationStatuses = {
	DRAFT: 'Draft',
	SHARED: 'Sent',
};

const ApplicationStatusBadge: React.FC<{ status: 'DRAFT' | 'SHARED' }> = ({ status }) => {
	return (
		<Box
			sx={{
				display: 'inline-block',
				padding: '0 8px',
				borderRadius: '20px',
				...(status === 'SHARED'
					? {
							color: '#164CB0',
							backgroundColor: '#EFF4FF',
						}
					: {
							color: '#2B2B2B',
							backgroundColor: '#F3F3F3',
						}),
			}}
		>
			{applicationStatuses[status]}
		</Box>
	);
};

export const ApplicationsList: React.FC = () => {
	const { defs } = useDefinitions();

	const navigate = useNavigate();

	const applicationsList = useApplicationsList();
	const applications = applicationsList.data ?? [];
	const hasApplications = applications.length > 0;

	const openApplication = async (applicationObjectId: string) => {
		const nodeId = await getNodeIdByObjectUri(applicationObjectId);
		navigate(`/application/${nodeId}`);
	};

	if (!applicationsList.isPending && !hasApplications) {
		return <AddApplicationForm />;
	}

	return (
		<TFDataTable<ProfileWithStatus>
			name="ca-applications-list"
			hideSearchbar
			isLoading={applicationsList.isPending}
			data={applications}
			defs={[
				{
					header: 'Name',
					accessorKey: 'labels.name',
					Cell: ({ row, renderedCellValue }) => {
						return (
							<Button
								variant="transparent"
								size="xs"
								sx={{ padding: 0, fontWeight: 'normal' }}
								onClick={() => openApplication(row.original.id)}
							>
								{renderedCellValue}
							</Button>
						);
					},
				},
				{
					header: 'Type',
					id: 'kind',
					accessorFn: (row) => {
						return row.kind ? defs.entities[row.kind].label : '';
					},
				},
				{
					header: 'Status',
					accessorKey: 'sharingStatus',
					Cell: ({ row }) => {
						return <ApplicationStatusBadge status={row.original.sharingStatus} />;
					},
				},
				{
					header: 'Created at',
					id: 'createdAt',
					accessorFn: (row) => {
						return row.meta ? fmt.toDate(row.meta['@createdAt'], { preset: 'full_date' }) : '';
					},
				},
			]}
		/>
	);
};
